<script lang="ts">
  import {
    byteLabel,
    fetchManifest,
    fetchModels,
    fetchStorage,
    leadLabel,
    type ManifestStep,
    type ModelInfo,
    type RunManifest,
    type StorageReport,
  } from '../../api/models'
  import { colorFor } from '../../chart/palette'
  import { getLayerDef } from '../../map/layers/registry'
  import { layers } from '../../state/layers.svelte'
  import { models } from '../../state/models.svelte'
  import Icon from '../Icon.svelte'
  import Panel from '../Panel.svelte'

  /**
   * What the model pipeline actually has, in words.
   *
   * This panel exists because **a reflectivity field is mostly empty**, and an
   * empty overlay is indistinguishable from a broken pipeline — the failure
   * mode this codebase has already been bitten by twice. "12Z run, 49 hours,
   * f+13: max 62.5 dBZ, 1.9% of the grid at or above 20 dBZ" is a quiet day.
   * "0 hours" or "no cells above the floor in any hour" is a bug. They should
   * not look alike from the couch.
   *
   * It also names the run's age, because a six-hour-old run drawn as if it
   * were current is the same class of lie that Phase 2.5 exists to prevent —
   * with the difference that here we know exactly how old it is, so we say.
   *
   * And it names the run that is *coming*, which the age alone says nothing
   * about. "The 20Z run, 2 h old" is the same sentence from a worker that is
   * forty minutes into the 21Z cycle and from one that died at 20:30 — and on
   * a six-hourly model it is the same sentence for five hours whatever is
   * happening. So the next cycle, how much of it has landed, and when this
   * deployment expects the rest are a line of their own.
   */

  interface ModelRow {
    info: ModelInfo
    manifest: RunManifest | null
  }

  /**
   * A clock the panel reads, rather than `Date.now()` at render time.
   *
   * Two numbers here are relative to now and nothing else on the page moves
   * them: the run's age and the countdown to the next one. The catalog poller
   * deliberately does not report a cycle boundary that changed nothing on disk
   * — rebuilding a CONUS texture for it would be a fetch for nothing — so
   * without this the panel would sit on "in 3 m" until a step landed.
   */
  let now = $state(Date.now())

  $effect(() => {
    const tick = setInterval(() => (now = Date.now()), 30_000)
    return () => clearInterval(tick)
  })

  let rows = $state<ModelRow[] | null>(null)
  /** When the catalog was last read, for the line at the top. */
  let checkedAt = $state<Date | null>(null)
  let failed = $state(false)
  let storage = $state<StorageReport | null>(null)

  // Model rows the user has actually switched on, so the panel describes what
  // is drawn rather than the whole catalog.
  let activeModels = $derived(
    new Set(
      [...layers.active]
        .map(getLayerDef)
        .map((d) => (d?.readout?.kind === 'model' ? d.readout : null))
        .filter((r): r is { kind: 'model'; model: string; variable: string } => r !== null)
        .map((r) => r.model),
    ),
  )

  /** The instant the slider is on, which is what the frame line describes. */
  let frame = $derived(layers.times[Math.min(layers.index, layers.times.length - 1)] ?? null)

  /**
   * Whether the panel can be seen. The catalog is polled for the map anyway,
   * but this panel also reads every model's manifest and walks the storage
   * tree, on every poll, so collapsed it asks for none of that.
   */
  let open = $state(false)

  $effect(() => {
    if (!open) return
    // Re-read whenever the poller says the catalog moved. This panel is the
    // one place that says "none complete enough to draw yet", so it is the one
    // that must stop saying it the minute that stops being true.
    void models.revision
    // And on a poll that moved nothing, because "next run" and its countdown
    // are about the clock rather than about what is on disk: a cycle boundary
    // changes this panel and changes nothing on the map.
    void models.polled
    let cancelled = false
    void (async () => {
      const found = await fetchModels()
      if (cancelled) return
      if (found.length === 0) {
        failed = true
        rows = []
        return
      }
      const built = await Promise.all(
        found.map(async (info) => ({
          info,
          manifest: info.latest ? await fetchManifest(info.id, info.latest) : null,
        })),
      )
      if (!cancelled) {
        failed = false
        rows = built
        checkedAt = new Date()
      }
      // After the manifests, not with them: it walks a directory tree, and a
      // panel that says nothing until the walk finishes is worse than one that
      // fills in.
      const report = await fetchStorage()
      if (!cancelled) storage = report
    })()
    return () => {
      cancelled = true
    }
  })

  const hourMinute = new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })

  // No weekday: everything this one prints is a few hours away at most, and a
  // "Sat" in front of it reads as a date rather than as a time today.
  const clock = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' })

  /** "12Z" — the cycle, which is how anyone refers to a model run out loud. */
  function cycleLabel(run: string): string {
    return `${run.slice(9, 11)}Z`
  }

  function runInstant(run: string): Date | null {
    // `20260918T12Z`, the same segment the URLs use.
    const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})Z$/.exec(run)
    if (!m) return null
    return new Date(
      Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4])),
    )
  }

  /** A duration a person reads: `40 m`, `2 h`, `2 h 13 m`. */
  function span(ms: number): string {
    const minutes = Math.max(0, Math.round(ms / 60_000))
    if (minutes < 60) return `${minutes} m`
    const hours = Math.floor(minutes / 60)
    const rest = minutes % 60
    return rest === 0 ? `${hours} h` : `${hours} h ${rest} m`
  }

  function age(run: string): string {
    const at = runInstant(run)
    return at ? `${span(now - at.getTime())} old` : ''
  }

  /**
   * What is coming, in the terms the panel can defend.
   *
   * The cycle and the estimate are the server's (`/api/models`): which cycles
   * this deployment ingests is a setting, and how long a run takes *here* is
   * measured from the runs on disk rather than from NOAA's figure for the
   * whole cycle. The progress is the catalog's own `runs` entry, matched by
   * id, so the step counts exist once.
   *
   * `serving` is the case with no gap in it: a run can be servable — every
   * field contiguous to `WEATHER_MODEL_MIN_SERVABLE_LEAD` — while it is still
   * publishing, so the run being drawn and the run being waited for are
   * regularly the same one. Calling that "Next" would be a lie about a run
   * already on the map.
   */
  function upcoming(info: ModelInfo) {
    const next = info.next_run
    if (!next) return null
    const due = new Date(next.expected).getTime()
    if (!Number.isFinite(due)) return null
    const entry = info.runs.find((r) => r.run === next.run) ?? null
    const want = entry?.expected_steps ?? 0
    const have = entry?.steps ?? 0
    const late = due < now
    return {
      cycle: cycleLabel(next.run),
      serving: next.run === info.latest,
      progress:
        want > 0 ? { have, want, pct: Math.min(100, Math.round((have / want) * 100)) } : null,
      // Which number this is, said out loud. "Usually" is what the retained
      // runs did here; "expected" is NOAA's published latency for the whole
      // cycle, which is all there is to go on before anything has finished.
      eta:
        `${next.basis === 'measured' ? 'usually complete by' : 'expected by'} ` +
        `${clock.format(due)} · ${late ? `${span(now - due)} late` : `in ${span(due - now)}`}`,
      late,
    }
  }

  /** The manifest step nearest the slider — the frame actually on screen. */
  function shownStep(manifest: RunManifest, variable: string): ManifestStep | null {
    const steps = manifest.variables?.[variable]?.steps ?? []
    if (steps.length === 0) return null
    if (!frame) return steps[0]
    let best = steps[0]
    let delta = Infinity
    for (const step of steps) {
      const d = Math.abs(new Date(step.valid).getTime() - frame.getTime())
      if (d < delta) {
        delta = d
        best = step
      }
    }
    return best
  }

  /**
   * "+0 h to +48 h, hourly" — what this run's axis actually is.
   *
   * Seven models no longer share one: GFS goes 3-hourly after f120, NBM starts
   * at f001 and steps 6-hourly past f192, and an accumulation has no step at
   * lead 0 at all. A count of steps says none of that, so the panel states the
   * span and the spacing from the declared list itself.
   */
  function axis(declared: readonly number[]): string {
    if (declared.length === 0) return ''
    const first = declared[0]
    const last = declared[declared.length - 1]
    const gaps = new Set<number>()
    for (let i = 1; i < declared.length; i++) gaps.add(declared[i] - declared[i - 1])
    const spacing =
      gaps.size === 1 ? `every ${leadLabel([...gaps][0]).replace('+', '')}` : 'mixed steps'
    return `${leadLabel(first)} to ${leadLabel(last)}, ${spacing}`
  }

  /**
   * The line section 3.5 of the plan is about.
   *
   * `covered` and the threshold counts are the difference between a quiet day
   * and a dead pipeline. A run with hours in it and nothing above the floor in
   * any of them says so in words rather than as an empty map.
   */
  function coverage(step: ManifestStep, units: string): string {
    const max = step.stats?.max
    const min = step.stats?.min
    const covered = step.stats?.covered ?? 0
    if (max === null || max === undefined) return 'no data in this step'

    // A field with no floor counts nothing, because there is nothing to
    // count: every cell of a temperature grid is a measurement, so "100% of
    // the grid is above 20 °F" would be a sentence that says nothing. Its
    // range is the informative thing instead. That is *not* the same as a
    // reflectivity frame whose counts all came back zero, which is the
    // "nothing above the floor" case below — so the test is whether the
    // variable declared any thresholds, not whether they found anything.
    const above = step.stats?.above ?? {}
    if (Object.keys(above).length === 0) {
      return min === null || min === undefined
        ? `max ${round(max)} ${units}`
        : `${round(min)} to ${round(max)} ${units} across the grid`
    }
    if (covered === 0) return `max ${round(max)} ${units} · nothing above the floor`

    const share = shareAbove(step)
    return share
      ? `max ${round(max)} ${units} · ${(share.fraction * 100).toFixed(1)}% ≥ ${share.threshold} ${units}`
      : `max ${round(max)} ${units} · ${(covered * 100).toFixed(1)}% of the grid`
  }

  /**
   * The share of the grid at or above the second-lowest counted threshold.
   *
   * Derived from `covered` and the ratio of two counts rather than from a grid
   * size: the manifest states the counts and the covered fraction, and the
   * grid's dimensions are the backend's business. Restating 1799 x 1059 here
   * would be a second copy of a number that lives in `app/models/registry.py`.
   */
  function shareAbove(step: ManifestStep): { threshold: string; fraction: number } | null {
    const above = step.stats?.above ?? {}
    const covered = step.stats?.covered ?? 0
    const keys = Object.keys(above).sort((a, b) => Number(a) - Number(b))
    if (keys.length < 2) return null
    const atFloor = above[keys[0]]
    const higher = above[keys[1]]
    if (!atFloor || higher === undefined) return null
    return { threshold: keys[1], fraction: (higher / atFloor) * covered }
  }

  function round(value: number): string {
    return Math.abs(value) >= 10 ? String(Math.round(value)) : value.toFixed(1)
  }

  /** What this model is using on disk, or '' before `/api/storage` lands. */
  function bytesFor(modelId: string): string {
    const found = storage?.models.find((m) => m.id === modelId)
    return found ? byteLabel(found.bytes) : ''
  }

  /** The catalog's entry for the run being drawn. */
  function latestRun(info: ModelInfo) {
    return info.runs.find((r) => r.run === info.latest) ?? null
  }

  /**
   * The tag, as round 2 draws it. Late wins: a run past the time this
   * deployment usually finishes by is the state worth seeing. Arriving is a
   * drawn run still filling, or a newer one on its way in.
   */
  function stateOf(
    info: ModelInfo,
    next: ReturnType<typeof upcoming>,
  ): 'complete' | 'arriving' | 'late' | null {
    if (next?.late) return 'late'
    const run = latestRun(info)
    if (!run) return null
    if (!run.complete || next?.serving || (next?.progress?.have ?? 0) > 0) return 'arriving'
    return 'complete'
  }

  /** How far this run animates without a gap, which is not how many steps it
   *  has: steps publish out of order on every model probed. */
  function reach(info: ModelInfo): string {
    const run = info.runs.find((r) => r.run === info.latest)
    if (!run) return ''
    if (run.complete) return 'complete'
    return run.contiguous_lead === null ? 'no steps yet' : `to ${leadLabel(run.contiguous_lead)}`
  }
</script>

<Panel id="models" title="Model status" bind:open>
  {#if rows === null}
    <p class="muted">Checking…</p>
  {:else if failed || rows.length === 0}
    <!-- An API-only deployment has no worker, which is a state rather than an
         error. Say which it is instead of leaving an empty panel. -->
    <p class="muted">No model data on this server yet.</p>
  {:else}
    <p class="checked">
      Checked {checkedAt ? clock.format(checkedAt) : '—'} · every minute while this is open
    </p>
    {#each rows as row (row.info.id)}
      {@const active = activeModels.has(row.info.id)}
      {@const next = upcoming(row.info)}
      {@const run = latestRun(row.info)}
      {@const state = stateOf(row.info, next)}
      <section class="model" class:active>
        <div class="head">
          <span class="key" style:background={colorFor('model', row.info.id)}></span>
          <span class="name">{row.info.label}</span>
          {#if state === 'late'}
            <span class="tag late"><Icon name="triangle-alert" size={11} strokeWidth={2.5} />Late</span>
          {:else if state === 'arriving'}
            <span class="tag arriving">Arriving</span>
          {:else if state === 'complete'}
            <span class="tag">Complete</span>
          {/if}
        </div>

        {#if row.info.latest}
          <div class="line">
            <span><b>{cycleLabel(row.info.latest)} run</b> · {age(row.info.latest)}</span>
            <span class="count">
              {#if run && run.expected_steps > 0}
                {run.steps} of {run.expected_steps} steps
              {:else}
                {reach(row.info)}
              {/if}
            </span>
          </div>
          {#if run && run.expected_steps > 0}
            <!-- Always ink: the series hue is on the key and nowhere else. -->
            <div
              class="bar"
              role="progressbar"
              aria-label="{cycleLabel(row.info.latest)} run"
              aria-valuemin="0"
              aria-valuemax={run.expected_steps}
              aria-valuenow={run.steps}
            >
              <span style:width="{Math.min(100, (run.steps / run.expected_steps) * 100)}%"></span>
            </div>
          {/if}
        {:else}
          <div class="line muted"><span>no servable run</span></div>
        {/if}

        <!-- What is coming. Without it the panel describes a still frame: a
             worker mid-cycle and one that died half an hour ago print the same
             sentence, and on a six-hourly model they print it for five hours. -->
        <div class="line next" class:late={next?.late}>
          <span>
            {#if next}
              {next.serving ? `${next.cycle} · still filling` : `Next ${next.cycle}`}
              {#if !next.serving}
                · {next.progress ? `${next.progress.have} of ${next.progress.want} steps` : 'no steps yet'}
              {/if}
              · {next.eta}
            {:else}
              {reach(row.info)}
            {/if}
          </span>
          <span class="size">{bytesFor(row.info.id)}</span>
        </div>

        {#if row.manifest}
          <!-- The line that makes an empty map legible, per field. Open by
               itself for a model that is on the map, which is when an empty
               plate needs explaining. -->
          <details class="fields" open={active}>
            <summary>
              {row.info.variables.length} field{row.info.variables.length === 1 ? '' : 's'} in this run
            </summary>
            {#each row.info.variables as variable (variable.id)}
              {@const entry = row.manifest.variables?.[variable.id]}
              {@const steps = entry?.steps ?? []}
              {@const declared = entry?.declared ?? []}
              {@const step = shownStep(row.manifest, variable.id)}
              <div class="field">
                <div class="line">
                  <span class="label">{variable.label}</span>
                  <span class="count">
                    {steps.length} of {declared.length || row.manifest.expected_steps} steps
                  </span>
                </div>
                <!-- What the axis is, not just how many of it there are:
                     seven models no longer share one. -->
                {#if declared.length > 0}
                  <div class="sub">{axis(declared)}</div>
                {/if}
                {#if step}
                  <div class="sub">
                    {leadLabel(step.lead)}, valid {hourMinute.format(new Date(step.valid))}
                  </div>
                  <div class="sub stats">{coverage(step, variable.units)}</div>
                {:else}
                  <div class="sub">no steps ingested yet</div>
                {/if}
              </div>
            {/each}
            <p class="sub attrib">{row.info.attribution}</p>
          </details>
        {:else if row.info.runs.length > 0}
          <p class="sub">
            {row.info.runs.length} run{row.info.runs.length === 1 ? '' : 's'} on disk, none
            complete enough to draw yet.
          </p>
        {:else}
          <p class="sub">Nothing ingested. Is the worker running?</p>
        {/if}
      </section>
    {/each}

    {#if storage}
      <div class="storage" class:over={storage.over_budget}>
        <div class="line">
          <b>Storage</b>
          <span class="count"
            ><b>{byteLabel(storage.total_bytes)}</b>{storage.budget_bytes > 0
              ? ` of ${byteLabel(storage.budget_bytes)}`
              : ''}</span
          >
        </div>
        {#if storage.budget_bytes > 0}
          <div class="bar" aria-hidden="true">
            <span
              style:width="{Math.min(100, (storage.total_bytes / storage.budget_bytes) * 100)}%"
            ></span>
          </div>
        {/if}
        <!-- Advisory: nothing deletes on it. A retention rule that deletes on
             a size trigger is one that deletes the run somebody is looking at. -->
        <p class="sub">
          {storage.over_budget
            ? 'Over budget; nothing was deleted. The budget is advisory.'
            : 'The budget is advisory: retention keeps the newest complete runs, never a size.'}
        </p>
      </div>
    {/if}
  {/if}
</Panel>

<style>
  .checked {
    margin: 0;
    padding-bottom: 8px;
    border-bottom: 2px solid var(--color-text);
    font-size: 11px;
    color: var(--color-muted);
  }

  .model {
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: 9px 0 10px;
    border-bottom: 1px solid var(--color-divider);
    font-variant-numeric: tabular-nums;
  }

  .head {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .key {
    flex: none;
    width: 14px;
    height: 3px;
  }

  .name {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    font-weight: 800;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 1px 5px;
    border: 2px solid var(--color-divider);
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .tag.arriving {
    border-color: var(--color-text);
    background: var(--color-text);
    color: var(--color-bg);
  }

  .tag.late {
    border-color: var(--color-warn);
    color: var(--color-warn);
  }

  .line {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 8px;
    font-size: 12px;
  }

  .line b {
    font-weight: 800;
  }

  .count,
  .size {
    flex: none;
    white-space: nowrap;
  }

  .size {
    color: var(--color-muted);
  }

  .next.late > span:first-child {
    color: var(--color-warn);
    font-weight: 800;
  }

  .bar {
    height: 6px;
    background: color-mix(in srgb, var(--color-text) 14%, transparent);
  }

  .bar span {
    display: block;
    height: 100%;
    background: var(--color-text);
  }

  .fields {
    margin-top: 2px;
  }

  .fields summary {
    cursor: pointer;
    font-size: 11px;
    font-weight: 600;
    color: var(--color-muted);
  }

  .field {
    padding: 6px 0 0 12px;
  }

  .label {
    font-weight: 600;
  }

  .sub {
    margin: 1px 0 0;
    font-size: 11px;
    line-height: 1.4;
    color: var(--color-muted);
  }

  .field .sub {
    margin-left: 0;
  }

  .attrib {
    padding: 6px 0 0 12px;
  }

  .storage {
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding-top: 10px;
  }

  .storage.over .sub {
    color: var(--color-warn);
    font-weight: 800;
  }
</style>
